import { combineLatest, map, Observable, debounceTime, filter } from 'rxjs';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { loadedState$ } from 'loadedState/loadedState$';
import { context$ } from 'streams/context/context$';
import {
  isTraversalLoadedState,
  TraversalLoadedState,
  LoadedState,
  ViewIds,
} from '@ardoq/api-types';
import { presentationMode$ } from 'views/streams/presentationMode$';
import { distinctUntilChanged, distinctUntilKeyChanged } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { activeView$ } from 'streams/views/mainContent/activeView$';
import {
  action$,
  ofType,
  extractPayload,
  streamReducer,
  reducedStream,
} from '@ardoq/rxbeach';
import { updateViewEmptyStateData } from './actions';
import type { EmptyStateProps } from '@ardoq/status-ui';
import { openViewSettingsPanel } from 'viewSettings/openViewSettingsPanel';
import {
  emptyStateCommands,
  emptyStatePresets,
  OnButtonClick,
} from 'tabview/graphViews/emptyStates/EmptyStateLayout';
import {
  UpdateViewEmptyStateDataPayload,
  AppEmptyStateData,
  ViewpointEmptyStateData,
  ViewSpecificEmptyStateData,
} from './types';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { globalPaneLoader$ } from 'components/GlobalPaneLoader/globalPaneLoader$';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { dispatchAction } from '@ardoq/rxbeach';
import { isBubbleChartEmptyStateData } from '@ardoq/bubble-chart';
import { type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

const getHasFilters = (
  traversalLoadedStates: TraversalLoadedState[]
): boolean =>
  traversalLoadedStates.some(loadedState =>
    Object.values(loadedState.data?.filters ?? {}).some(
      filter => filter.rules.length > 0
    )
  );

const getHasRequiredPaths = (
  traversalLoadedStates: TraversalLoadedState[]
): boolean =>
  traversalLoadedStates.some(
    loadedState =>
      loadedState.data?.paths?.some(path =>
        path.some(triple => triple.qualifier === 'required')
      ) ?? false
  );

const getTraversalLoadedStates = (
  loadedStates: LoadedState[]
): TraversalLoadedState[] =>
  loadedStates.filter(loadedState => isTraversalLoadedState(loadedState));

const appGlobalEmptyStateData$ = combineLatest([
  loadedState$,
  loadedGraph$,
  presentationMode$,
  context$,
]).pipe(
  distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
  map(
    ([
      { loadedStates },
      loadedGraph,
      { isPresentationMode },
      { workspaceId },
    ]) => {
      const { scopeComponentIds, isViewpointMode } = loadedGraph;
      const isDatasetEmpty = !scopeComponentIds.length;
      const isSomeDatasetHidden = loadedStates.some(({ isHidden }) => isHidden);
      const traversalLoadedStates = getTraversalLoadedStates(loadedStates);
      const hasFilters =
        isSomeDatasetHidden || getHasFilters(traversalLoadedStates);
      const hasRequiredPaths = getHasRequiredPaths(traversalLoadedStates);

      return {
        isDatasetEmpty,
        hasRequiredPaths,
        isSomeDatasetHidden,
        hasFilters,
        isPresentationMode,
        loadedStates,
        workspaceId,
        isViewpointMode,
      };
    }
  )
);

const handleUpdateViewEmptyStateData = (
  state: ViewSpecificEmptyStateData,
  { viewEmptyStateData, viewId }: UpdateViewEmptyStateDataPayload
): ViewSpecificEmptyStateData => ({
  ...state,
  viewEmptyStateData,
  payloadViewId: viewId,
});

const updateViewEmptyStateData$ = action$.pipe(
  ofType(updateViewEmptyStateData),
  extractPayload(),
  debounceTime(DEFAULT_DEBOUNCE_TIME * 2),
  distinctUntilChanged((a, b) => isEqual(a, b))
);

const mainViewChanged$ = activeView$.pipe(
  distinctUntilKeyChanged('mainViewId')
);

const handleMainViewChanged = (
  state: ViewSpecificEmptyStateData,
  { mainViewId }: { mainViewId: ViewIds }
): ViewSpecificEmptyStateData => {
  if (mainViewId !== state.payloadViewId) {
    // If the main view is not the empty state view, reset the empty state data
    return {
      activeViewId: mainViewId,
      payloadViewId: mainViewId,
      emptyStateLayout: null,
      viewEmptyStateData: { isViewEmpty: false },
    };
  }

  return {
    ...state,
    activeViewId: mainViewId,
  };
};

const viewSpecificEmptyStateReducers = [
  streamReducer(mainViewChanged$, handleMainViewChanged),
  streamReducer(updateViewEmptyStateData$, handleUpdateViewEmptyStateData),
];

const defaultViewSpecificEmptyState: ViewSpecificEmptyStateData = {
  activeViewId: ViewIds.NONE,
  payloadViewId: ViewIds.NONE,
  emptyStateLayout: null,
  viewEmptyStateData: { isViewEmpty: false },
};

const viewSpecificEmptyState$ = reducedStream<ViewSpecificEmptyStateData>(
  'viewSpecificEmptyState$',
  defaultViewSpecificEmptyState,
  viewSpecificEmptyStateReducers
);

const buildEmptyStateLayoutBasedOnDataset = (
  appData: AppEmptyStateData,
  editDataset: OnButtonClick,
  i18nLocale: I18nLocale
): EmptyStateProps => {
  const { isDatasetEmpty, hasRequiredPaths, hasFilters, isPresentationMode } =
    appData;

  const showActionControls = !isPresentationMode;

  /**
   * Contributors that are part of the discover journey are not allowed to
   * create or edit datasets, so this empty state is shown instead of the default
   * empty state that allows editing the dataset
   */
  if (isDatasetEmpty && isDiscoverJourney()) {
    return emptyStatePresets.noDataAvailableInDiscoverJourney({
      handleNavigateToDiscoverHomeClick: () => {
        dispatchAction(
          navigate({
            route: '/discover',
            params: {},
          })
        );
      },
      i18nLocale,
    });
  }

  if (hasRequiredPaths) {
    return emptyStatePresets.noDataInRequiredPaths({
      editDataset,
      showActionControls,
      i18nLocale,
    });
  }

  if (isDatasetEmpty) {
    return emptyStatePresets.noDataAvailable({
      editDataset,
      showActionControls,
      i18nLocale,
    });
  }

  if (hasFilters) {
    return emptyStatePresets.dataFiltered({
      editDataset,
      showActionControls,
      i18nLocale,
    });
  }

  return emptyStatePresets.noDataAvailable({
    editDataset,
    showActionControls,
    i18nLocale,
  });
};

const buildEmptyStateLayout = (
  viewData: ViewSpecificEmptyStateData,
  appData: AppEmptyStateData,
  i18nLocale: I18nLocale
): EmptyStateProps | null => {
  const { viewEmptyStateData, activeViewId } = viewData;
  const { loadedStates, isPresentationMode, workspaceId } = appData;

  if (!viewEmptyStateData.isViewEmpty) {
    return null;
  }

  const editDataset = emptyStateCommands.getEditDatasetAction(loadedStates[0]);

  switch (activeViewId) {
    case ViewIds.BUBBLE: {
      if (isBubbleChartEmptyStateData(viewEmptyStateData)) {
        const {
          noNumericFields,
          selectedComponentHasNumericFields,
          hasOnlyPointsWithZeroRadius,
        } = viewEmptyStateData;

        if (appData.isDatasetEmpty) {
          break;
        }

        const openManageFieldsModal =
          emptyStateCommands.getOpenManageFieldsModalAction(workspaceId);
        const showActionControls = !isPresentationMode;

        if (hasOnlyPointsWithZeroRadius) {
          if (viewEmptyStateData.hasSelectedComponentType) {
            return emptyStatePresets.onlyZeroValuesForBubbleSizeSelectedComponent(
              {
                openBubbleChartSettings: openViewSettingsPanel,
                i18nLocale,
              }
            );
          }

          return emptyStatePresets.onlyZeroValuesForBubbleSize({
            editDataset,
            openBubbleChartSettings: openViewSettingsPanel,
            i18nLocale,
          });
        }

        if (noNumericFields) {
          return emptyStatePresets.noNumericFields({
            editDataset,
            openManageFieldsModal,
            showActionControls,
            i18nLocale,
          });
        }

        if (!noNumericFields && !selectedComponentHasNumericFields) {
          return emptyStatePresets.noNumericFieldsInComponentType({
            openViewSettingsPanel,
            openManageFieldsModal,
            showActionControls,
            i18nLocale,
          });
        }

        if (viewEmptyStateData.onChangeFieldsClick) {
          if (viewEmptyStateData.hasSelectedComponentType) {
            return emptyStatePresets.noViewpointBubbleFieldsSelectedComponent({
              onBubbleChartSettings: viewEmptyStateData.onChangeFieldsClick,
              i18nLocale,
            });
          }

          return emptyStatePresets.noViewpointBubbleFields({
            onChangeSelectedFields: viewEmptyStateData.onChangeFieldsClick,
            i18nLocale,
          });
        }
      }
      break;
    }
    default: {
      break;
    }
  }

  return buildEmptyStateLayoutBasedOnDataset(appData, editDataset, i18nLocale);
};

// Combined stream that debounces based on loading state and view changes
// Prevents flickering of empty state when loading data
const debouncedViewState$: Observable<
  [AppEmptyStateData, ViewSpecificEmptyStateData, I18nLocale]
> = combineLatest([
  globalPaneLoader$,
  appGlobalEmptyStateData$,
  viewSpecificEmptyState$,
  i18nLocale$,
]).pipe(
  debounceTime(DEFAULT_DEBOUNCE_TIME), // debounce for all changes
  filter(([{ isLoading }]) => !isLoading), // Only emit when not loading
  map(
    ([, appData, viewData, i18nLocale]) =>
      [appData, viewData, i18nLocale] as [
        AppEmptyStateData,
        ViewSpecificEmptyStateData,
        I18nLocale,
      ]
  )
);

export const viewpointEmptyState$: Observable<ViewpointEmptyStateData> =
  debouncedViewState$.pipe(
    map(([appData, viewData, i18nLocale]) => {
      if (!appData.isViewpointMode) {
        return {
          emptyStateLayout: null,
        };
      }

      const emptyStateLayout = buildEmptyStateLayout(
        viewData,
        appData,
        i18nLocale
      );

      return {
        emptyStateLayout,
      };
    })
  );
